import { addImportantToEachRule, buildSlottedStyles, color as colorUtils, getCss, buildResponsiveStyles, mergeDeep, } from "../../utils";
import { getStateMessageStyles } from "../common/state-message/state-message-styles";
/**
 * Slotted styles (applied when using the *native select element*).
 *
 * @param el
 * @param useNativeSelectEl
 * @param labelPosition
 * @param validationstate
 */
export const getSlottedCss = (el, useNativeSelectEl, labelPosition, validationstate) => {
    const nativeElTopPadding = labelPosition === 'default' ? 26 : 7;
    const nativeElBottomPadding = labelPosition === 'default' ? 11 : 7;
    return getCss(buildSlottedStyles(el, addImportantToEachRule({
        '& select': Object.assign(Object.assign({}, (useNativeSelectEl
            ? {
                appearance: 'none',
                WebkitAppearance: 'none',
                position: 'absolute',
                inset: '0',
                width: '100%',
                margin: '0',
                border: 'none',
                background: 'none',
                padding: `${nativeElTopPadding}px 48px ${nativeElBottomPadding}px 16px`,
                fontFamily: 'Bosch Sans',
                fontSize: '14px',
                lineHeight: '16px',
                letterSpacing: '0.16px',
                color: colorUtils.darkBlue02,
                cursor: 'pointer',
            }
            : {})), { '&:disabled': {
                borderColor: colorUtils.grey02,
                backgroundColor: colorUtils.grey02,
                color: colorUtils.grey03,
                cursor: 'not-allowed',
            }, '&:focus': validationstate === 'error'
                ? {
                    outline: `2px solid ${colorUtils.turquoise03}`,
                    outlineOffset: '2px',
                }
                : {
                    outline: 'none',
                    borderColor: colorUtils.turquoise03,
                    boxShadow: `inset 0 0 0 1px ${colorUtils.turquoise03}`,
                }, '& option': {
                fontFamily: '"Bosch Sans", sans-serif',
            } }),
    })));
};
/**
 * Styles (for the custom dropdown element, and for the native select wrapper if using the native select element).
 *
 * @param labelPosition
 * @param disabled
 * @param useNativeSelectEl
 * @param validationstate
 * @param labelWidth
 * @param open
 */
export const createStyles = (labelPosition, disabled, useNativeSelectEl, validationstate, labelWidth, open) => {
    const verticalPadding = labelPosition === 'default' ? 10 : 7;
    const styles = getCss(Object.assign({ '@global': {
            '::slotted': addImportantToEachRule({
                '&(select)': useNativeSelectEl
                    ? {}
                    : {
                        display: 'none',
                    },
            }),
            '.container': Object.assign(Object.assign({ fontFamily: 'Bosch Sans', fontSize: '14px', lineHeight: '16px', letterSpacing: '0.16px' }, (labelPosition === 'left'
                ? Object.assign(Object.assign({ display: 'grid' }, mergeDeep(buildResponsiveStyles(labelWidth, (labelWidthResponsive) => ({
                    gridTemplateColumns: `${labelWidthResponsive} minmax(0, 1fr)`,
                })))), { gridTemplateRows: 'auto auto', columnGap: '8px' }) : {})), { '& label': Object.assign({ display: 'block', fontFamily: 'Bosch Sans', fontSize: '12px', lineHeight: '15px', letterSpacing: '0.32px', textTransform: 'uppercase', color: disabled ? colorUtils.grey03 : colorUtils.grey06, whiteSpace: 'nowrap' }, (labelPosition === 'left'
                    ? {
                        alignSelf: 'center',
                    }
                    : labelPosition === 'above'
                        ? {
                            marginBottom: '8px',
                        }
                        : {
                            marginBottom: '1px',
                            pointerEvents: 'none',
                        })), '& > *': {
                    maxWidth: '100%',
                } }),
            '.dropdown-head': {
                display: 'grid',
                position: 'relative',
                width: '100%',
                boxSizing: 'border-box',
                border: `1px solid ${colorUtils.grey05}`,
                backgroundColor: colorUtils.white,
                padding: `${verticalPadding}px 48px ${verticalPadding}px 16px`,
                fontFamily: 'Bosch Sans',
                fontSize: '14px',
                lineHeight: '16px',
                letterSpacing: '0.16px',
                textAlign: 'left',
                color: colorUtils.darkBlue02,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': {
                    borderColor: colorUtils.grey10,
                },
                '&:not(.disabled, :disabled):focus': {
                    outline: 'none',
                    borderColor: colorUtils.turquoise03,
                    boxShadow: `inset 0 0 0 1px ${colorUtils.turquoise03}`,
                },
                '&:not(.disabled, :disabled).error': {
                    outline: 'none',
                    borderColor: colorUtils.red02,
                    boxShadow: `inset 0 0 0 1px ${colorUtils.red02}`,
                },
                '&:not(.disabled, :disabled).error:focus': {
                    outline: `2px solid ${colorUtils.turquoise03}`,
                    outlineOffset: '1px',
                },
                '&.disabled, &:disabled': {
                    borderColor: colorUtils.grey02,
                    backgroundColor: colorUtils.grey02,
                    color: colorUtils.grey03,
                    cursor: 'not-allowed',
                },
            },
            '.native-select-wrapper': {
                height: labelPosition === 'default' ? '54px' : '32px',
                '& label': {
                    position: 'relative',
                    zIndex: 1,
                },
            },
            '.dropdown': {
                position: 'relative',
            },
            '.dropdown-head-text': {
                display: 'block',
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                outline: 'none',
            },
            '.arrow-icon': {
                position: 'absolute',
                top: '50%',
                right: '16px',
                transform: `translateY(-50%) ${open ? 'rotateX(180deg)' : ''}`,
                transition: 'transform 350ms',
            },
            'ul, li': {
                margin: '0',
                padding: '0',
                listStyleType: 'none',
            },
            '.dropdown-list': useNativeSelectEl
                ? {
                    display: 'none',
                }
                : {
                    visibility: 'hidden',
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    width: '100%',
                    maxHeight: labelPosition === 'default' ? '208px' : '196px',
                    overflowY: 'auto',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                    zIndex: '999',
                    pointerEvents: 'none',
                    '&.open': {
                        visibility: 'visible',
                        pointerEvents: 'unset',
                    },
                },
            '.dropdown-option': {
                backgroundColor: colorUtils.white,
                padding: `${labelPosition === 'default' ? 11 : 7}px 17px`,
                color: colorUtils.darkBlue02,
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: colorUtils.grey01,
                },
                '&.active': {
                    boxShadow: `inset 0 0 0 2px ${colorUtils.turquoise03}`,
                },
                '& .checkmark-icon': {
                    display: 'none',
                },
            },
            '.dropdown-list.has-optgroups .dropdown-option': {
                paddingLeft: '32px',
                '&[aria-selected="true"]': {
                    position: 'relative',
                    '& .checkmark-icon': {
                        display: 'inline-flex',
                        position: 'absolute',
                        top: '12px',
                        left: '8px',
                    },
                },
            },
            '.dropdown-optgroup-heading': {
                backgroundColor: colorUtils.white,
                padding: `${labelPosition === 'default' ? 11 : 7}px 17px`,
                color: colorUtils.grey06,
            },
            '.message': {
                gridColumnStart: '2',
            },
        } }, getStateMessageStyles(validationstate)));
    return styles;
};
//# sourceMappingURL=dropdown-wrapper-styles.js.map
